<template>
  <div class="file-print-preview">
    <div class="flex flex-col gap-4 items-center">
      <div
        class="relative w-full bg-surface-variant rounded-lg overflow-hidden"
      >
        <canvas
          ref="pdfCanvas"
          class="block max-w-full h-auto border-2 border-gray-300 rounded-lg bg-white mx-auto"
        />
        <div
          v-if="pdfRenderer.isLoading.value"
          class="absolute inset-0 flex items-center justify-center bg-white/90"
        >
          <div class="text-center">
            <CircularSpinner />
            <p class="text-sm text-gray-600 mt-2">Loading PDF...</p>
          </div>
        </div>
        <div
          v-else-if="pdfRenderer.error.value"
          class="absolute inset-0 flex items-center justify-center bg-white/90 text-error"
        >
          <div class="text-center">
            <Icon :icon="ChevronLeftIcon" class="w-8 h-8 mx-auto mb-2" />
            <p class="text-sm">{{ pdfRenderer.error.value }}</p>
          </div>
        </div>
        <div
          v-if="!pdfRenderer.isLoading.value && !pdfRenderer.error.value"
          class="absolute inset-0"
        >
          <ClientOnly v-if="isCustomizable">
            <PdfQuadrantsOverlay
              ref="quadrantsOverlay"
              :stage-config="stageConfig"
              :transformer-config="transformerConfig"
              :state="quadrantState"
              :interactions="quadrantInteractions"
              @logo-click="handleLogoClick"
              @logo-remove="handleLogoRemove"
              @logo-transform-end="handleLogoTransformEnd"
              @logo-drag-end="handleLogoDragEnd"
            />
          </ClientOnly>
          <PdfPageNavigation
            :current-page="pdfRenderer.currentPage.value"
            :total-pages="pdfRenderer.totalPages.value"
            @previous-page="handlePreviousPage"
            @next-page="handleNextPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted } from 'vue';
import { Icon } from '~/components/icon';
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { CircularSpinner } from '~/components/loaders';

import { useQuadrantsStore } from '~/store/quadrants-store';
import { usePdfRenderer } from './composables/use-pdf-renderer';
import {
  usePdfQuadrants,
  type SimpleQuadrant,
  type KonvaNode,
  type CustomizationEvent,
} from './composables/use-pdf-quadrants';
import PdfPageNavigation from './components/pdf-page-navigation.vue';
import ChevronLeftIcon from '@/assets/icons/chevron-down.svg?component';

const PdfQuadrantsOverlay = defineAsyncComponent(
  () => import('./components/pdf-quadrants-overlay.vue'),
);

type Props = {
  fileUrl: string;
  contentId: number;
  fileId: number;
  isCustomizable?: boolean;
  selectedLogoId?: string;
  quadrants?: Array<{
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    page: number;
  }>;
};

const emit = defineEmits([
  'update:customization',
  'personalization-changed',
  'customization-ready',
  'canvas-dimensions-changed',
]);

const props = withDefaults(defineProps<Props>(), {
  isCustomizable: false,
  selectedLogoId: '',
  quadrants: () => [],
});

const quadrantsStore = useQuadrantsStore();
const { t } = useI18n();

const pdfCanvas = ref<HTMLCanvasElement | null>(null);
const quadrantsOverlay = ref<any>(null);

let autoSaveHandler = () => {
  /* placeholder */
};
let transformerUpdateHandler = () => {
  /* placeholder */
};

const quadrantManager = usePdfQuadrants({
  onQuadrantsChanged: () => {
    if (quadrantsOverlay.value?.konvaStage) {
      const stage = quadrantsOverlay.value.konvaStage.getStage();
      stage?.batchDraw();
    }
  },
  onCustomizationChange: (event) => {
    emit('update:customization', event);
  },
  onPersonalizationChange: (data) => {
    emit('personalization-changed', data);
  },
  onAutoSave: autoSaveHandler,
  onTransformerUpdate: transformerUpdateHandler,
});

const pdfRenderer = usePdfRenderer({
  onPageRendered: () => {
    nextTick(() => {
      if (props.isCustomizable) {
        quadrantManager.loadQuadrantsWhenReady({
          propsQuadrants: props.quadrants,
          currentPage: pdfRenderer.currentPage.value,
          canvasWidth: pdfRenderer.canvasWidth.value,
          canvasHeight: pdfRenderer.canvasHeight.value,
          originalPageWidth: pdfRenderer.originalPageWidth.value,
          originalPageHeight: pdfRenderer.originalPageHeight.value,
        });

        nextTick(() => {
          quadrantManager.restoreLogoStatesAndBounds(
            pdfRenderer.currentPage.value,
          );

          setTimeout(() => {
            const interactions = quadrantManager.getInteractions();
            const occupancy =
              quadrantManager.getState().quadrantOccupancy.value;
            for (const [quadrantId, logoNode] of occupancy.entries()) {
              if (logoNode) {
                interactions.establishLogoHomeQuadrant(logoNode);
              }
            }
          }, 200);
        });
      }
    });
  },
});

const quadrantState = computed(() => quadrantManager.getState());
const quadrantInteractions = computed(() => quadrantManager.getInteractions());

autoSaveHandler = () => {
  const state = quadrantManager.getState();
  const isPersonalizedLocal =
    state.customerLogo.value && state.logoConfigs.value.length > 0;

  if (isPersonalizedLocal) {
    const allLogoStates = quadrantManager.getAllLogoStatesAcrossPages(
      pdfRenderer.currentPage.value,
    );
    const interactions = quadrantManager.getInteractions();

    const customizationData = {
      logos: allLogoStates.length > 0 ? allLogoStates : state.logoConfigs.value,
      logoId: props.selectedLogoId || '',
      quadrantPositions: state.simpleQuadrants.value.map((q) => ({
        id: q.id,
        occupied: interactions.isQuadrantOccupied(q),
      })),
    };

    emit('update:customization', {
      action: 'auto-save' as const,
      data: customizationData,
      contentId: props.contentId,
      fileId: props.fileId,
      page: pdfRenderer.currentPage.value,
    });
  }
};

transformerUpdateHandler = () => {
  const transformer = quadrantsOverlay.value?.konvaTransformer?.getNode();

  if (transformer) {
    transformer.nodes([]);

    const state = quadrantManager.getState();
    if (state.selectedLogoRef.value) {
      transformer.nodes([state.selectedLogoRef.value]);
      transformer.forceUpdate();
      transformer.getLayer()?.batchDraw();
    }
  }
};

const isPersonalized = () => {
  return (
    quadrantState.value.customerLogo.value &&
    quadrantState.value.logoConfigs.value.length > 0
  );
};

const isQuadrantOccupied = (quadrant: SimpleQuadrant) => {
  return quadrantInteractions.value.isQuadrantOccupied(quadrant);
};

const updateTransformer = () => {
  const transformer = quadrantsOverlay.value?.konvaTransformer?.getNode();

  if (transformer) {
    transformer.nodes([]);

    const state = quadrantManager.getState();
    if (state.selectedLogoRef.value) {
      transformer.nodes([state.selectedLogoRef.value]);
      transformer.forceUpdate();
      transformer.getLayer()?.batchDraw();
    }
  }
};

const autoSaveDraft = () => {
  const state = quadrantManager.getState();
  const isPersonalizedLocal =
    state.customerLogo.value && state.logoConfigs.value.length > 0;

  if (!isPersonalizedLocal) {
    return null;
  }

  const allLogoStates = quadrantManager.getAllLogoStatesAcrossPages(
    pdfRenderer.currentPage.value,
  );
  const interactions = quadrantManager.getInteractions();

  const customizationData = {
    logos: allLogoStates.length > 0 ? allLogoStates : state.logoConfigs.value,
    logoId: props.selectedLogoId || '',
    quadrantPositions: state.simpleQuadrants.value.map((q) => ({
      id: q.id,
      occupied: interactions.isQuadrantOccupied(q),
    })),
  };

  emit('update:customization', {
    action: 'auto-save' as const,
    data: customizationData,
    contentId: props.contentId,
    fileId: props.fileId,
    page: pdfRenderer.currentPage.value,
  });

  return customizationData;
};

const hideTransformer = () => {
  const transformerNode = quadrantsOverlay.value?.konvaTransformer?.getNode();

  if (transformerNode) {
    transformerNode.nodes([]);
  }
};

const stageConfig = computed(() => ({
  width: pdfRenderer.canvasWidth.value,
  height: pdfRenderer.canvasHeight.value,
  scaleX: 1,
  scaleY: 1,
}));

const transformerConfig = computed(() => {
  const config = {
    centeredScaling: false,
    rotateEnabled: false,
    keepRatio: true,
    enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
    boundBoxFunc: quadrantInteractions.value.createResizeBoundFunc(),
    ignoreStroke: true,
    anchorSize: 10,
    anchorCornerRadius: 2,
    borderStroke: '#3b82f6',
    borderStrokeWidth: 2,
    anchorFill: '#3b82f6',
    anchorStroke: '#1e40af',
    anchorStrokeWidth: 1,
    forceUpdate: true,
  } as const;

  return config;
});

const handleStageMouseDown = (e: any) => {
  const { target } = e;
  if (target === e.target.getStage()) {
    hideTransformer();
    quadrantState.value.isLogoSelected.value = false;
    quadrantState.value.selectedLogoRef.value = null;
  }
};

const handleLogoClick = (logoElement: any) => {
  updateTransformer();
};

const handleLogoRemove = (quadrantId: string | number) => {
  quadrantInteractions.value.handleLogoRemove(quadrantId);
  hideTransformer();
  autoSaveDraft();
};

const handleLogoDragEnd = (logoElement: any) => {
  quadrantInteractions.value.handleLogoDrag(logoElement);

  if (props.isCustomizable) {
    quadrantManager.saveLogoStatesForPage(pdfRenderer.currentPage.value);
  }

  setTimeout(() => {
    quadrantInteractions.value.establishLogoHomeQuadrant(logoElement);
  }, 10);

  autoSaveDraft();
};

const handleLogoTransformEnd = (logoElement: any) => {
  if (props.isCustomizable) {
    quadrantManager.saveLogoStatesForPage(pdfRenderer.currentPage.value);
  }

  setTimeout(() => {
    quadrantInteractions.value.establishLogoHomeQuadrant(logoElement);
  }, 10);

  autoSaveDraft();
};

const handleNextPage = async () => {
  if (props.isCustomizable) {
    hideTransformer();
    const currentPageBeforeSwitch = pdfRenderer.currentPage.value;
    quadrantManager.saveLogoStatesForPage(currentPageBeforeSwitch);
  }

  await pdfRenderer.nextPage();
};

const handlePreviousPage = async () => {
  if (props.isCustomizable) {
    hideTransformer();
    const currentPageBeforeSwitch = pdfRenderer.currentPage.value;
    quadrantManager.saveLogoStatesForPage(currentPageBeforeSwitch);
  }

  await pdfRenderer.previousPage();
};

const resetCustomization = () => {
  quadrantManager.clearCustomization();
  emit('update:customization', {
    action: 'reset' as const,
    page: pdfRenderer.currentPage.value,
  });
};

const getCurrentCustomization = () => {
  if (!isPersonalized()) return null;

  const allLogoStates = quadrantManager.getAllLogoStatesAcrossPages(
    pdfRenderer.currentPage.value,
  );

  return {
    logos:
      allLogoStates.length > 0
        ? allLogoStates
        : quadrantState.value.logoConfigs.value,
    logoId: props.selectedLogoId || '',
    isPersonalized: true,
  };
};

const getOriginalDimensions = () => {
  return {
    width: pdfRenderer.originalPageWidth.value,
    height: pdfRenderer.originalPageHeight.value,
  };
};

const getCanvasDisplayDimensions = () => {
  return {
    width: pdfRenderer.canvasWidth.value,
    height: pdfRenderer.canvasHeight.value,
  };
};

defineExpose({
  getCurrentCustomization,
  getOriginalDimensions,
  getCanvasDisplayDimensions,
  resetCustomization,
  autoSaveDraft,
});

await quadrantsStore.getLogos();

onMounted(async () => {
  pdfRenderer.pdfCanvas.value = pdfCanvas.value;
  await quadrantsStore.getLogos();
  if (pdfRenderer.pdfCanvas.value) {
    await pdfRenderer.loadPDF(props.fileUrl);
  }
});

watch(
  () => props.selectedLogoId,
  (newLogoId) => {
    if (newLogoId) {
      const logoUrl = quadrantsStore.getLogoById(newLogoId);
      if (logoUrl) {
        quadrantState.value.customerLogo.value = {
          fileUrl: logoUrl,
          accountLogoId: newLogoId,
        };
        quadrantManager.loadLogoImage(logoUrl);
      }
    } else {
      quadrantManager.clearCustomization();
    }
  },
);

watch(
  () => props.fileUrl,
  async () => {
    if (props.fileUrl && pdfCanvas.value) {
      pdfRenderer.pdfCanvas.value = pdfCanvas.value;
      quadrantManager.resetQuadrantsScaling();
      await pdfRenderer.loadPDF(props.fileUrl);
    }
  },
);

watch(
  () => props.quadrants,
  () => {
    quadrantManager.resetQuadrantsScaling();
    if (props.isCustomizable) {
      quadrantManager.loadQuadrantsWhenReady({
        propsQuadrants: props.quadrants,
        currentPage: pdfRenderer.currentPage.value,
        canvasWidth: pdfRenderer.canvasWidth.value,
        canvasHeight: pdfRenderer.canvasHeight.value,
        originalPageWidth: pdfRenderer.originalPageWidth.value,
        originalPageHeight: pdfRenderer.originalPageHeight.value,
      });
    }
  },
  { immediate: true },
);

watch(
  () => quadrantState.value.logoConfigs.value,
  (newConfigs) => {
    if (newConfigs.length > 0) {
      quadrantState.value.quadrantOccupancy.value.clear();
      quadrantState.value.logoHomeQuadrants.value.clear();

      setTimeout(() => {
        autoSaveDraft();
      }, 1000);
    }
  },
  { deep: true },
);

watch(
  () => quadrantState.value.customerLogo.value,
  (newLogo) => {
    if (newLogo) {
      setTimeout(() => {
        autoSaveDraft();
      }, 500);
    } else {
      quadrantState.value.quadrantOccupancy.value.clear();
      quadrantState.value.logoHomeQuadrants.value.clear();
    }
  },
);

watch(
  () => isPersonalized(),
  (newState) => {
    emit('personalization-changed', {
      isPersonalized: newState,
      customization: newState ? getCurrentCustomization() : null,
    });
  },
  { immediate: true },
);

watch(
  [
    () => pdfRenderer.canvasWidth.value as number,
    () => pdfRenderer.canvasHeight.value as number,
    () => pdfRenderer.originalPageWidth.value as number,
    () => pdfRenderer.originalPageHeight.value as number,
  ],
  ([width, height, originalWidth, originalHeight]) => {
    if (width > 0 && height > 0) {
      emit('canvas-dimensions-changed', {
        width,
        height,
        originalWidth,
        originalHeight,
      });
    }
  },
  { immediate: true },
);

watch(
  () => getCurrentCustomization(),
  (customization) => {
    if (customization) {
      emit('customization-ready', customization);
    }
  },
  { deep: true },
);
</script>

<style scoped>
.file-print-preview {
  @apply w-full;
}

canvas {
  background: white !important;
  border: 2px solid #d1d5db !important;
}

.customize-instructions {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
